import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages the add-to-cart modal with product recommendations.
 * Opens automatically when the main product is added to cart.
 *
 * @extends {Component}
 */
class AddToCartModalComponent extends Component {
  requiredRefs = /** @type {any[]} */ ([]);

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {AbortController | null} For recommendation fetches */
  #recommendationsFetch = null;

  /** @type {Set<string>} Tracked recommended product IDs */
  #recommendedProductIds = new Set();

  /** @type {Set<string>} Selected product IDs for add to cart */
  #selectedProductIds = new Set();

  /** @type {Map<string, number>} Product prices in cents (productId -> price) */
  #productPrices = new Map();

  /** @type {Map<string, number>} Product compare at prices in cents (productId -> compareAtPrice) */
  #productCompareAtPrices = new Map();

  /** @type {string | null} Main product ID */
  #mainProductId = null;

  /** @type {string | null} Section ID for cart updates */
  #sectionId = null;

  /** @type {Record<string, string>} Cached recommendations */
  #cachedRecommendations = {};

  /** @type {boolean} Whether modal is already opening/opened */
  #isOpening = false;

  /** @type {number | null} Debounce timer for subtotal updates */
  #subtotalUpdateTimer = null;

  /** @type {string | null} Original product variant ID that triggered the modal */
  #originalVariantId = null;

  /** @type {number | null} Original product line item number in cart */
  #originalLineItem = null;

  /** @type {string | null} Original product cart item key (unique identifier) */
  #originalItemKey = null;

  /** @type {string[] | null} Allowed variant titles (null means all variants allowed) */
  #allowedVariantTitles = null;

  /** @type {Map<string, string>} Map of variant ID to variant title */
  #variantTitlesMap = new Map();

  /**
   * Gets the modal wrapper element
   * @returns {HTMLElement | null}
   */
  #getWrapper() {
    // First check if it's still in the component
    const wrapperInComponent = this.querySelector('.add-to-cart-modal-wrapper');
    if (wrapperInComponent instanceof HTMLElement) return wrapperInComponent;
    
    // If not found, check document.body using data-component-id attribute
    const wrapperInBody = document.querySelector(`.add-to-cart-modal-wrapper[data-component-id="${this.id}"]`);
    return wrapperInBody instanceof HTMLElement ? wrapperInBody : null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Get data attributes
    this.#mainProductId = this.dataset.productId || null;
    this.#sectionId = this.dataset.sectionId || null;

    // Parse allowed variant titles
    const allowedTitlesStr = this.dataset.allowedVariantTitles;
    if (allowedTitlesStr && allowedTitlesStr.trim() !== '') {
      this.#allowedVariantTitles = allowedTitlesStr
        .split(',')
        .map(title => title.trim())
        .filter(title => title !== '');
    }

    // Parse variant titles map
    const variantTitlesMapStr = this.dataset.variantTitlesMap;
    if (variantTitlesMapStr) {
      try {
        const map = JSON.parse(variantTitlesMapStr);
        for (const [variantId, title] of Object.entries(map)) {
          this.#variantTitlesMap.set(String(variantId), String(title));
        }
      } catch (error) {
        console.warn('Failed to parse variant titles map:', error);
      }
    }

    // Listen for form submit events to open modal immediately
    document.addEventListener('submit', this.#handleFormSubmit);
    // Listen for cart add events
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // Listen for cart update events to refresh the summary
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    
    // Use document-level event delegation for cart button (works even when wrapper is moved to body)
    document.addEventListener('click', this.#handleCartButtonClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('submit', this.#handleFormSubmit);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener('click', this.#handleCartButtonClick);
    
    // Clean up subtotal update timer
    if (this.#subtotalUpdateTimer) {
      clearTimeout(this.#subtotalUpdateTimer);
      this.#subtotalUpdateTimer = null;
    }
  }

  /**
   * Loads product recommendations and extracts product IDs
   */
  async #loadRecommendations() {
    const wrapper = this.#getWrapper();
    if (!wrapper) {
      console.warn('Wrapper not found');
      return;
    }
    
    const recommendationsElement = wrapper.querySelector('.product-recommendations');
    if (!(recommendationsElement instanceof HTMLElement)) {
      console.warn('Recommendations element not found');
      return;
    }

    const { productId, sectionId, intent, url } = recommendationsElement.dataset;
    const id = recommendationsElement.id;

    if (!productId || !id || !url) {
      console.warn('Missing required data for recommendations:', { productId, id, url });
      return;
    }

    // If recommendations already loaded, just extract IDs
    if (recommendationsElement.dataset.recommendationsPerformed === 'true') {
      this.#extractProductIdsFromElement(recommendationsElement);
      // Check if there are products after extracting IDs
      this.#checkAndCloseIfNoProducts(recommendationsElement, intent);
      return;
    }

    try {
      if (!url || !productId || !id) {
        console.warn('Missing data before fetch');
        return;
      }
      const result = await this.#fetchCachedRecommendations(url, productId, sectionId || undefined, intent || undefined, id);
      
      if (!result.success) {
        console.error('Failed to fetch recommendations:', result);
        // Check if there are products even if fetch failed
        this.#checkAndCloseIfNoProducts(recommendationsElement, intent);
        return;
      }

      const html = document.createElement('div');
      html.innerHTML = result.data || '';
      const recommendations = html.querySelector(`.product-recommendations[id="${id}"]`);

      if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
        recommendationsElement.dataset.recommendationsPerformed = 'true';
        recommendationsElement.innerHTML = recommendations.innerHTML;
        
        this.#extractProductIdsFromElement(recommendationsElement);
        
        // Check if there are products after loading
        this.#checkAndCloseIfNoProducts(recommendationsElement, intent);
      } else {
        console.warn('No recommendations content found in response');
        // Check if there are products even if no content found
        this.#checkAndCloseIfNoProducts(recommendationsElement, intent);
      }
    } catch (error) {
      console.error('Error loading recommendations:', error);
      // Check if there are products even if error occurred
      this.#checkAndCloseIfNoProducts(recommendationsElement, intent);
    }
  }

  /**
   * Fetches recommendations and caches the result
   * @param {string} baseUrl - Base URL for recommendations
   * @param {string} productId - Product ID
   * @param {string | undefined} sectionId - Section ID
   * @param {string | undefined} intent - Recommendation intent
   * @param {string} id - Element ID
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(baseUrl, productId, sectionId, intent, id) {
    const url = `${baseUrl}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#recommendationsFetch?.abort();
    this.#recommendationsFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#recommendationsFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#recommendationsFetch = null;
    }
  }

  /**
   * Checks if there are products available (before opening modal)
   * @returns {Promise<boolean>} True if products are available, false otherwise
   */
  async #checkIfHasProducts() {
    const wrapper = this.#getWrapper();
    if (!wrapper) {
      return false;
    }
    
    const recommendationsElement = wrapper.querySelector('.product-recommendations');
    if (!(recommendationsElement instanceof HTMLElement)) {
      return false;
    }

    const { productId, sectionId, intent, url } = recommendationsElement.dataset;
    const id = recommendationsElement.id;

    if (!productId || !id) {
      return false;
    }

    // If recommendations already loaded server-side, check the data-has-recommendations attribute
    if (recommendationsElement.dataset.recommendationsPerformed === 'true') {
      const productsList = recommendationsElement.querySelector('.add-to-cart-modal__products-list');
      const hasRecommendationsAttr = productsList?.getAttribute('data-has-recommendations');
      
      // Also check for actual product cards
      const productCards = recommendationsElement.querySelectorAll('.add-to-cart-modal__product-card[data-product-id]:not(.add-to-cart-modal__product-card--skeleton)');
      const hasProductCards = productCards.length > 0;
      
      return hasRecommendationsAttr === 'true' || hasProductCards;
    }

    // If recommendations need to be fetched, check cache first, then fetch if needed
    if (!url) {
      return false;
    }

    try {
      // Build the URL to check cache
      const fullUrl = `${url}&product_id=${productId}&section_id=${sectionId || ''}&intent=${intent || ''}`;
      
      // Check cache first
      if (this.#cachedRecommendations[fullUrl]) {
        const html = document.createElement('div');
        html.innerHTML = this.#cachedRecommendations[fullUrl];
        const recommendations = html.querySelector(`.product-recommendations[id="${id}"]`);
        
        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          const productCards = recommendations.querySelectorAll('.add-to-cart-modal__product-card[data-product-id]');
          return productCards.length > 0;
        }
        return false;
      }
      
      // If not cached, fetch them
      const result = await this.#fetchCachedRecommendations(url, productId, sectionId || undefined, intent || undefined, id);
      
      if (!result.success) {
        return false;
      }

      const html = document.createElement('div');
      html.innerHTML = result.data || '';
      const recommendations = html.querySelector(`.product-recommendations[id="${id}"]`);

      if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
        // Check if the fetched HTML contains product cards
        const productCards = recommendations.querySelectorAll('.add-to-cart-modal__product-card[data-product-id]');
        return productCards.length > 0;
      }

      return false;
    } catch (error) {
      console.error('Error checking for products:', error);
      return false;
    }
  }

  /**
   * Checks if there are products and closes the modal if none are found
   * @param {HTMLElement} recommendationsElement - The product-recommendations element
   * @param {string | undefined} intent - The recommendation intent ('complementary' or 'related')
   */
  #checkAndCloseIfNoProducts(recommendationsElement, intent) {
    if (!recommendationsElement) return;
    
    // Check if recommendations have been performed
    const recommendationsPerformed = recommendationsElement.dataset.recommendationsPerformed === 'true';
    
    // Check the data-has-recommendations attribute (set by Liquid)
    const productsList = recommendationsElement.querySelector('.add-to-cart-modal__products-list');
    const hasRecommendationsAttr = productsList?.getAttribute('data-has-recommendations');
    
    // Check for actual product cards (not skeleton/loading cards)
    const productCards = recommendationsElement.querySelectorAll('.add-to-cart-modal__product-card[data-product-id]:not(.add-to-cart-modal__product-card--skeleton)');
    const hasProductCards = productCards.length > 0;
    
    // Check for skeleton cards to know if we're still loading
    const skeletonCards = recommendationsElement.querySelectorAll('.add-to-cart-modal__product-card--skeleton');
    const isLoading = skeletonCards.length > 0;
    
    // If still loading, don't check yet
    if (isLoading && !recommendationsPerformed) {
      return;
    }
    
    // Determine if there are products
    // If recommendations are performed, check actual cards
    // Otherwise, check the Liquid-set attribute
    const hasProducts = recommendationsPerformed 
      ? hasProductCards 
      : (hasRecommendationsAttr === 'true' || hasProductCards);
    
    // If no products found, close the modal
    if (!hasProducts) {
      // Small delay to ensure UI is ready
      setTimeout(() => {
        this.closeModal(new Event('auto-close-no-products'));
      }, 100);
    }
  }

  /**
   * Extracts product IDs from the recommendations element DOM
   * @param {HTMLElement} recommendationsElement - The product-recommendations element
   */
  #extractProductIdsFromElement(recommendationsElement) {
    // Check product cards
    const productCards = recommendationsElement.querySelectorAll('.add-to-cart-modal__product-card[data-product-id]');
    productCards.forEach((card) => {
      if (card instanceof HTMLElement) {
        const productId = card.getAttribute('data-product-id');
        if (productId) {
          this.#recommendedProductIds.add(String(productId));
          // Extract price for this product
          this.#extractProductPrice(productId);
        }
      }
    });

    // Also check inputs/checkboxes
    const productInputs = recommendationsElement.querySelectorAll('.add-to-cart-modal__radio-input[data-product-id]');
    productInputs.forEach((input) => {
      if (input instanceof HTMLElement) {
        const productId = input.getAttribute('data-product-id');
        if (productId) {
          this.#recommendedProductIds.add(String(productId));
          // Extract price for this product
          this.#extractProductPrice(productId);
        }
      }
    });
  }

  /**
   * Checks if a variant is allowed based on variant titles filter
   * @param {string} variantId - The variant ID to check
   * @returns {boolean} True if variant is allowed, false otherwise
   */
  #isVariantAllowed(variantId) {
    // If no filter is set, allow all variants
    if (!this.#allowedVariantTitles || this.#allowedVariantTitles.length === 0) {
      return true;
    }

    // Get variant title from map
    const variantTitle = this.#variantTitlesMap.get(String(variantId));
    if (!variantTitle) {
      // If variant title not found in map, allow it (fallback behavior)
      return true;
    }

    // Check if variant title matches any of the allowed titles
    return this.#allowedVariantTitles.some(allowedTitle => 
      variantTitle.toLowerCase().trim() === allowedTitle.toLowerCase().trim()
    );
  }

  /**
   * Handles form submit events to open modal immediately
   * @param {Event} event - The submit event
   */
  #handleFormSubmit = async (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    
    // Check if this is an add-to-cart form
    const form = event.target;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return;
    
    // Check if this is for the main product
    const productFormComponent = form.closest('product-form-component');
    if (!(productFormComponent instanceof HTMLElement)) return;
    
    const formProductId = productFormComponent.dataset.productId;
    if (!this.#mainProductId || !formProductId || String(formProductId) !== String(this.#mainProductId)) {
      return;
    }

    // Try to get variant ID from form
    const variantIdInput = form.querySelector('[name="id"]') || form.querySelector('[ref="variantId"]');
    let variantId = null;
    if (variantIdInput instanceof HTMLInputElement) {
      variantId = variantIdInput.value;
    } else if (variantIdInput instanceof HTMLElement) {
      variantId = variantIdInput.textContent;
    } else {
      console.warn('[BUNDLE] Could not find variant ID input in form');
    }

    // Check if variant is allowed
    if (variantId && !this.#isVariantAllowed(variantId)) {
      return;
    }

    if (variantId) {
      this.#originalVariantId = String(variantId);
    }
    
    // Check if there are products before opening modal
    if (!this.#isOpening) {
      this.#isOpening = true;
      const hasProducts = await this.#checkIfHasProducts();
      if (hasProducts) {
        await this.#openModal();
      }
      // Reset flag after a short delay to allow for cart add event handling
      setTimeout(() => {
        this.#isOpening = false;
      }, 100);
    }
  };

  /**
   * Handles cart add events
   * @param {CartAddEvent} event - The cart add event
   */
  #handleCartAdd = async (event) => {
    if (!event.detail?.data) {
      return;
    }

    const { productId, source } = event.detail.data;

    // Skip if this event came from the modal itself to avoid loops
    if (source === 'add-to-cart-modal') {
      return;
    }

    // Check if main product was added - open modal (if not already opening/opened)
    if (this.#mainProductId && productId && String(productId) === String(this.#mainProductId)) {
      // Track the original variant ID from the event
      const variantId = event.detail.data?.variantId || event.detail.sourceId;
      
      // Check if variant is allowed
      if (variantId && !this.#isVariantAllowed(String(variantId))) {
        return;
      }

      if (variantId) {
        this.#originalVariantId = String(variantId);
        
        // Always fetch cart to get the item key (cart resource is not available in event)
        this.#fetchCartForTracking(variantId);
      } else {
        console.warn('[BUNDLE] No variant ID found in event data');
      }
      
      // Only open if not already opening (form submit might have already triggered it)
      const wrapper = this.#getWrapper();
      const isAlreadyOpen = wrapper?.classList.contains('open');
      if (!this.#isOpening && !isAlreadyOpen) {
        this.#isOpening = true;
        // Check if there are products before opening modal
        const hasProducts = await this.#checkIfHasProducts();
        if (hasProducts) {
          await this.#openModal();
        }
        this.#isOpening = false;
      }
      return;
    }

    // Clear selection after adding to cart if it was a recommended product
    if (productId && this.#recommendedProductIds.has(String(productId))) {
      this.#selectedProductIds.delete(String(productId));
      this.#setButtonSuccessState(String(productId), false);
      this.#updateBottomButtons();
    }

    // Update cart total when any product is added
    if (event.detail?.resource) {
      this.#enableCartSummaryShimmer();
      this.#updateCartTotal(event.detail.resource);
      // Small delay to show shimmer effect
      setTimeout(() => {
        this.#disableCartSummaryShimmer();
      }, 100);
    }
  };



  /**
   * Opens the modal
   */
  async #openModal() {
    const wrapper = this.#getWrapper();
    if (!(wrapper instanceof HTMLElement)) {
      this.#isOpening = false;
      return;
    }
    
    // Prevent duplicate opens
    if (wrapper.classList.contains('open')) {
      this.#isOpening = false;
      return;
    }

    // Append entire component to document body if not already there
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    // Create and append overlay to document body
    let overlay = document.querySelector('.add-to-cart-modal-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'add-to-cart-modal-overlay';
      overlay.addEventListener('click', (event) => {
        // Only close if clicking the overlay itself, not the modal content
        if (event.target === overlay) {
          this.closeModal(event);
        }
      });
      document.body.appendChild(overlay);
    }

    // Show the wrapper and overlay
    wrapper.classList.add('open');
    overlay.classList.add('open');

    // Check immediately if recommendations are already loaded (server-side)
    const recommendationsElement = wrapper.querySelector('.product-recommendations');
    if (recommendationsElement instanceof HTMLElement) {
      const intent = recommendationsElement.dataset.intent;
      if (recommendationsElement.dataset.recommendationsPerformed === 'true') {
        // Recommendations already loaded, check for products immediately
        this.#checkAndCloseIfNoProducts(recommendationsElement, intent);
      }
    }

    // Load recommendations and cart data in the background (non-blocking)
    this.#loadRecommendations().then(() => {
      // Set up observer for when recommendations load via the recommendations element
      const recommendationsElement = wrapper.querySelector('.product-recommendations');
      if (recommendationsElement instanceof HTMLElement) {
        const intent = recommendationsElement.dataset.intent;
        
        const observer = new MutationObserver(() => {
          this.#extractProductIdsFromElement(recommendationsElement);
          // Check for products after DOM changes
          this.#checkAndCloseIfNoProducts(recommendationsElement, intent);
        });

        observer.observe(recommendationsElement, {
          childList: true,
          subtree: true,
        });

        // Also listen for when recommendations are performed
        const recommendationsObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (
              mutation.type === 'attributes' &&
              mutation.attributeName === 'data-recommendations-performed' &&
              recommendationsElement.dataset.recommendationsPerformed === 'true'
            ) {
              this.#extractProductIdsFromElement(recommendationsElement);
              // Check for products after recommendations are performed
              this.#checkAndCloseIfNoProducts(recommendationsElement, intent);
            }
          }
        });

        recommendationsObserver.observe(recommendationsElement, {
          attributes: true,
          attributeFilter: ['data-recommendations-performed'],
        });
      }
    });

    // Update cart total in the background (non-blocking)
    this.#refreshCartTotal();
  }

  /**
   * Enables shimmer effect on button price
   */
  #enableCartSummaryShimmer() {
    const wrapper = this.#getWrapper();
    if (!wrapper) return;
    
    const addSelectedWrapper = wrapper.querySelector('.add-to-cart-modal__add-selected-wrapper');
    if (!addSelectedWrapper) return;
    
    const priceElement = addSelectedWrapper.querySelector('.price-container .price');
    if (priceElement instanceof HTMLElement) {
      priceElement.setAttribute('shimmer', '');
    }
  }

  /**
   * Disables shimmer effect on button price
   */
  #disableCartSummaryShimmer() {
    const wrapper = this.#getWrapper();
    if (!wrapper) return;
    
    const addSelectedWrapper = wrapper.querySelector('.add-to-cart-modal__add-selected-wrapper');
    if (!addSelectedWrapper) return;
    
    const priceElement = addSelectedWrapper.querySelector('.price-container .price');
    if (priceElement instanceof HTMLElement) {
      priceElement.removeAttribute('shimmer');
    }
  }

  /**
   * Refreshes the cart total from the current cart
   */
  async #refreshCartTotal() {
    if (!this.#sectionId) return;

    try {
      this.#enableCartSummaryShimmer();
      
      const abortController = this.#createAbortController();
      const config = fetchConfig('json', {
        body: JSON.stringify({
          sections: [this.#sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        this.#disableCartSummaryShimmer();
        return;
      }

      const data = await response.json();
      this.#updateCartTotal(data);
      this.#disableCartSummaryShimmer();
    } catch (error) {
      console.error('Error refreshing cart total:', error);
      this.#disableCartSummaryShimmer();
    }
  }


  /**
   * Handles cart update events to refresh the summary
   * @param {CartUpdateEvent} event - The cart update event
   */
  #handleCartUpdate = async (event) => {
    if (!(event instanceof CartUpdateEvent)) return;
    
    const cartData = event.detail?.resource;
    const itemCount = event.detail?.data?.itemCount;
    
    if (cartData) {
      this.#updateCartSummary(cartData, itemCount);
      this.#disableCartSummaryShimmer();
    } else if (this.#sectionId) {
      // If no cart data in event, fetch it
      await this.#refreshCartTotal();
    }
  };

  /**
   * Updates the cart total in the footer
   * @param {Object} cartData - Cart data from API response
   * @param {number} [cartData.total_price] - Total price in cents
   * @param {string} [cartData.currency] - Currency code
   */
  #updateCartTotal(cartData) {
    this.#updateCartSummary(cartData);
  }

  /**
   * Extracts product price from the DOM and stores it
   * @param {string} productId - The product ID
   */
  #extractProductPrice(productId) {
    const wrapper = this.#getWrapper();
    if (!wrapper) return;

    const productCard = wrapper.querySelector(`.add-to-cart-modal__product-card[data-product-id="${productId}"]`);
    if (!productCard) return;

    const priceElement = productCard.querySelector('.add-to-cart-modal__price-current');
    if (!priceElement) return;

    // First try to get price from data attribute (most reliable)
    const priceDataAttr = priceElement.getAttribute('data-price');
    if (priceDataAttr) {
      const priceInCents = parseInt(priceDataAttr, 10);
      if (!isNaN(priceInCents) && priceInCents > 0) {
        this.#productPrices.set(productId, priceInCents);
      }
    }

    // Extract compare at price if available
    const compareAtPriceDataAttr = priceElement.getAttribute('data-compare-at-price');
    if (compareAtPriceDataAttr) {
      const compareAtPriceInCents = parseInt(compareAtPriceDataAttr, 10);
      if (!isNaN(compareAtPriceInCents) && compareAtPriceInCents > 0) {
        this.#productCompareAtPrices.set(productId, compareAtPriceInCents);
      }
    }

    // Fallback: parse from text content if data attributes weren't available
    if (!priceDataAttr) {
      const priceText = priceElement.textContent?.trim() || '';
      if (priceText) {
        // Parse price - remove currency symbols and convert to cents
        // Handle formats like "€29,99", "29,99 €", "29.99", etc.
        const priceMatch = priceText.replace(/[^\d,.-]/g, '').replace(',', '.');
        const priceValue = parseFloat(priceMatch);
        
        if (!isNaN(priceValue) && priceValue > 0) {
          // Convert to cents
          const priceInCents = Math.round(priceValue * 100);
          this.#productPrices.set(productId, priceInCents);
        }
      }
    }
  }

  /**
   * Updates the subtotal to show selected products in the modal
   * This method calculates the subtotal based on selected checkboxes only
   * Uses debouncing to prevent too many rapid calls
   */
  #updateSelectedProductsSubtotal() {
    // Clear any pending debounce timer
    if (this.#subtotalUpdateTimer) {
      clearTimeout(this.#subtotalUpdateTimer);
      this.#subtotalUpdateTimer = null;
    }

    // Debounce the update to prevent too many rapid calls
    this.#subtotalUpdateTimer = setTimeout(() => {
      this.#subtotalUpdateTimer = null;
      this.#performSubtotalUpdate();
    }, 150); // 150ms debounce
  }

  /**
   * Performs the actual subtotal update based on selected products in the modal
   */
  #performSubtotalUpdate() {
    // Calculate total from selected products (checked checkboxes)
    let selectedProductsTotal = 0;
    let selectedProductsCompareAtTotal = 0;

    for (const productId of this.#selectedProductIds) {
      const price = this.#productPrices.get(productId);
      if (price) {
        selectedProductsTotal += price;
      }
      
      // Also track compare at price if available
      const compareAtPrice = this.#productCompareAtPrices.get(productId);
      if (compareAtPrice) {
        selectedProductsCompareAtTotal += compareAtPrice;
      }
    }

    // Use the active Shopify Markets presentment currency so the button
    // total matches the currency rendered in product cards. data-price is
    // already in the presentment currency's cents — no rate conversion needed.
    const shopifyCurrency = /** @type {{active?: string}} */ (
      /** @type {any} */ (window).Shopify?.currency
    );
    const currency = shopifyCurrency?.active || 'EUR';

    this.#updateButtonPrice(selectedProductsTotal, currency, selectedProductsCompareAtTotal > 0 ? selectedProductsCompareAtTotal : null);
  }

  /**
   * Updates the price in the add-selected button
   * @param {number} totalPriceInCents - Total price in cents
   * @param {string} currency - Currency code
   * @param {number | null} compareAtPriceInCents - Compare at price in cents (optional)
   */
  #updateButtonPrice(totalPriceInCents, currency = 'EUR', compareAtPriceInCents = null) {
    const wrapper = this.#getWrapper();
    if (!wrapper) {
      return;
    }

    const addSelectedWrapper = wrapper.querySelector('.add-to-cart-modal__add-selected-wrapper');
    if (!addSelectedWrapper) {
      return;
    }

    // Find the price container within the button
    const priceContainer = addSelectedWrapper.querySelector('.price-container');
    if (!priceContainer) {
      return;
    }

    const priceElement = priceContainer.querySelector('.price');
    if (!priceElement) {
      return;
    }

    const locale = document.documentElement.lang || 'fr-FR';

    // Format the prices
    const formattedPrice = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      trailingZeroDisplay: 'stripIfInteger',
    }).format(totalPriceInCents / 100);

    // Check if we should show compare at price
    const shouldShowCompareAtPrice = compareAtPriceInCents !== null && compareAtPriceInCents > totalPriceInCents;

    if (shouldShowCompareAtPrice) {
      // Find or create compare at price element
      let compareAtPriceElement = priceContainer.querySelector('.compare-at-price');
      let compareAtPriceGroup = compareAtPriceElement?.closest('span[role="group"]');
      
      if (!compareAtPriceElement || !compareAtPriceGroup) {
        // Create compare at price group if it doesn't exist
        compareAtPriceGroup = document.createElement('span');
        compareAtPriceGroup.setAttribute('role', 'group');
        
        const visuallyHidden = document.createElement('span');
        visuallyHidden.className = 'visually-hidden';
        visuallyHidden.textContent = 'Prix régulier ';
        compareAtPriceGroup.appendChild(visuallyHidden);
        
        compareAtPriceElement = document.createElement('span');
        compareAtPriceElement.className = 'compare-at-price';
        compareAtPriceGroup.appendChild(compareAtPriceElement);
        
        // Insert before the price element (or its group)
        const priceGroup = priceElement.closest('span[role="group"]');
        const insertBefore = priceGroup || priceElement;
        priceContainer.insertBefore(compareAtPriceGroup, insertBefore);
      }

      // Update compare at price
      const formattedCompareAtPrice = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        trailingZeroDisplay: 'stripIfInteger',
      }).format(compareAtPriceInCents / 100);
      
      compareAtPriceElement.textContent = formattedCompareAtPrice;

      // Ensure price is wrapped in a role="group" for accessibility (matching price snippet structure)
      let priceGroup = priceElement.closest('span[role="group"]');
      if (!priceGroup) {
        priceGroup = document.createElement('span');
        priceGroup.setAttribute('role', 'group');
        
        const visuallyHidden = document.createElement('span');
        visuallyHidden.className = 'visually-hidden';
        visuallyHidden.textContent = 'Prix de vente ';
        priceGroup.appendChild(visuallyHidden);
        
        // Insert price element into the group
        priceElement.parentElement?.insertBefore(priceGroup, priceElement);
        priceGroup.appendChild(priceElement);
      }
      
      // Ensure price has 'strong' class (matching price snippet)
      if (!priceElement.classList.contains('strong')) {
        priceElement.classList.add('strong');
      }
    } else {
      // Remove compare at price if it exists but shouldn't be shown
      const compareAtPriceElement = priceContainer.querySelector('.compare-at-price');
      if (compareAtPriceElement) {
        const priceGroup = compareAtPriceElement.closest('span[role="group"]');
        if (priceGroup) {
          priceGroup.remove();
        } else {
          compareAtPriceElement.remove();
        }
      }
      
      // Remove price group wrapper if it exists (simplify structure when no compare at price)
      const priceGroup = priceElement.closest('span[role="group"]');
      if (priceGroup && priceGroup.querySelector('.visually-hidden')?.textContent?.includes('Prix de vente')) {
        // Move price element out of group and remove group
        const parent = priceGroup.parentElement;
        parent?.insertBefore(priceElement, priceGroup);
        priceGroup.remove();
      }
    }

    // Update the price text
    priceElement.textContent = formattedPrice;
  }

  /**
   * Updates the cart summary when cart data is received
   * Simply recalculates based on selected products in the modal
   * @param {Object} cartData - Cart data from API response (not used, but kept for compatibility)
   * @param {number} [itemCount] - Number of items in cart (not used, but kept for compatibility)
   */
  #updateCartSummary(cartData, itemCount) {
    // Just recalculate based on selected products in the modal
    // No need to fetch or use cart data - we only show what's selected
    this.#performSubtotalUpdate();
  }

  /**
   * Opens the modal (public method for theme editor)
   */
  openModal = async () => {
    await this.#openModal();
  };

  /**
   * Closes the modal
   * @param {Event} event - The click event
   */
  closeModal = (event) => {
    event?.preventDefault();
    const wrapper = this.#getWrapper();
    const overlay = document.querySelector('.add-to-cart-modal-overlay');
    
    if (wrapper instanceof HTMLElement) {
      wrapper.classList.add('closing');
      if (overlay) {
        overlay.classList.add('closing');
        overlay.classList.remove('open');
      }
      // Reset opening flag
      this.#isOpening = false;
      // Remove closing class and hide after animation
      setTimeout(() => {
        wrapper.classList.remove('open', 'closing');
        // Remove overlay from document
        if (overlay) {
          overlay.remove();
        }
        // Note: Component stays in body when closed - it will be reused if opened again
        // This is fine since the component is hidden when wrapper doesn't have 'open' class
      }, 300); // Match animation duration
    }
  };

  /**
   * Handles clicks on the cart button using document-level event delegation
   * @param {Event} event - The click event
   */
  #handleCartButtonClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    
    // Check if the click is on the cart button or its child elements
    const cartButton = target.closest('.add-to-cart-modal__cart-button');
    
    // Verify the button belongs to this component's wrapper
    if (cartButton) {
      const wrapper = this.#getWrapper();
      if (wrapper && wrapper.contains(cartButton)) {
        event.stopPropagation(); // Prevent the on:click handler from also firing
        event.preventDefault();
        this.openCartDrawer(event);
      }
    }
  };

  /**
   * Opens the cart drawer
   * @param {Event} event - The click event
   */
  openCartDrawer = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    
    // Close the modal first
    this.closeModal(event);
    
    // Small delay to ensure modal closes before opening drawer
    setTimeout(() => {
      // Find and open the cart drawer
      const cartDrawer = document.querySelector('cart-drawer-component#cart-drawer');
      if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        console.warn('Cart drawer component not found');
      }
    }, 100);
  };

  /**
   * Toggles product selection when checkbox is changed
   * @param {Event} event - The change event
   */
  toggleProductSelection = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    const checkbox = event.target;
    
    if (checkbox.type !== 'checkbox') return;

    const productId = checkbox.dataset.productId;
    if (!productId) return;

    // Extract and store product price if not already stored
    if (!this.#productPrices.has(productId)) {
      this.#extractProductPrice(productId);
    }

    // Toggle selection based on checkbox checked state
    if (checkbox.checked) {
      this.#selectedProductIds.add(productId);
      this.#setButtonSuccessState(productId, true);
    } else {
      this.#selectedProductIds.delete(productId);
      this.#setButtonSuccessState(productId, false);
    }

    // Update bottom buttons based on selection
    this.#updateBottomButtons();
    // Update subtotal to reflect selected products
    this.#updateSelectedProductsSubtotal();
  };

  /**
   * Handles variant selection inside a product card.
   * Updates selected pill state, the hidden checkbox's data-variant-id and
   * availability, and swaps the displayed price.
   *
   * @param {Event} event
   */
  selectVariant = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const button = target.closest('.add-to-cart-modal__variant');
    if (!(button instanceof HTMLElement)) return;

    const card = button.closest('.add-to-cart-modal__product-card');
    if (!(card instanceof HTMLElement)) return;

    const productId = card.dataset.productId;
    if (!productId) return;

    let variantId, available, priceStr, compareStr, priceDisplay, compareDisplay;

    if (button.dataset.optionPosition) {
      // Multi-option mode: each pill is an option value, not a full variant.
      // Toggle selection inside the option group only, then resolve the full
      // combination against the injected JSON map.
      const group = button.closest('.add-to-cart-modal__option-group');
      if (group instanceof HTMLElement) {
        group.querySelectorAll('.add-to-cart-modal__variant').forEach((sibling) => {
          const isSelected = sibling === button;
          sibling.classList.toggle('add-to-cart-modal__variant--selected', isSelected);
          sibling.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
      }

      // Fieldset-sync mode: mirror the clicked value across every other option
      // group by matching data-option-value. Lets a single picker drive all
      // options of a bundle-style product (e.g. Duo Cushion Cream).
      const variantsContainer = button.closest('.add-to-cart-modal__variants');
      const syncEnabled =
        variantsContainer instanceof HTMLElement &&
        variantsContainer.dataset.syncVariantFieldsets === 'true';
      if (syncEnabled && group instanceof HTMLElement) {
        const pickedValue = button.dataset.optionValue ?? '';
        card.querySelectorAll('.add-to-cart-modal__option-group').forEach((otherGroup) => {
          if (otherGroup === group) return;
          let hasMatch = false;
          otherGroup.querySelectorAll('.add-to-cart-modal__variant').forEach((btn) => {
            const matches =
              btn instanceof HTMLElement && btn.dataset.optionValue === pickedValue;
            if (matches) hasMatch = true;
            btn.classList.toggle('add-to-cart-modal__variant--selected', matches);
            btn.setAttribute('aria-checked', matches ? 'true' : 'false');
          });
          // If no value matches in this other group, keep the previous selection
          // so we still resolve to *some* variant combination downstream.
          if (!hasMatch) {
            const previous = otherGroup.querySelector(
              '.add-to-cart-modal__variant[aria-checked="true"]'
            );
            if (previous instanceof HTMLElement) {
              previous.classList.add('add-to-cart-modal__variant--selected');
            }
          }
        });
      }

      const selectedValues = {};
      card.querySelectorAll('.add-to-cart-modal__option-group').forEach((g) => {
        const selected = g.querySelector('.add-to-cart-modal__variant--selected');
        if (selected instanceof HTMLElement && selected.dataset.optionPosition) {
          selectedValues[selected.dataset.optionPosition] = selected.dataset.optionValue ?? '';
        }
      });

      const dataScript = card.querySelector('.add-to-cart-modal__variants-data');
      let variants = [];
      if (dataScript instanceof HTMLElement) {
        try {
          variants = JSON.parse(dataScript.textContent || '[]');
        } catch (error) {
          console.warn('[modal] Failed to parse variants JSON', error);
        }
      }

      const matched = variants.find((v) =>
        Array.isArray(v.options) &&
        v.options.every((optVal, i) => selectedValues[String(i + 1)] === optVal)
      );

      if (!matched) {
        const checkbox = card.querySelector('.add-to-cart-modal__radio-input');
        if (checkbox instanceof HTMLInputElement) {
          checkbox.disabled = true;
          if (checkbox.checked) {
            checkbox.checked = false;
            this.#selectedProductIds.delete(productId);
            this.#setButtonSuccessState(productId, false);
            this.#updateBottomButtons();
            this.#updateSelectedProductsSubtotal();
          }
        }
        return;
      }

      variantId = String(matched.id);
      available = matched.available === true;
      priceStr = String(matched.price ?? '');
      compareStr = String(matched.compare ?? '0');
      priceDisplay = matched.priceDisplay ?? '';
      compareDisplay = matched.compareDisplay ?? '';
    } else {
      variantId = button.dataset.variantId;
      available = button.dataset.variantAvailable === 'true';
      priceStr = button.dataset.variantPrice;
      compareStr = button.dataset.variantCompareAtPrice;
      priceDisplay = button.dataset.variantPriceDisplay ?? '';
      compareDisplay = button.dataset.variantCompareDisplay ?? '';
      if (!variantId) return;

      card.querySelectorAll('.add-to-cart-modal__variant').forEach((sibling) => {
        const isSelected = sibling === button;
        sibling.classList.toggle('add-to-cart-modal__variant--selected', isSelected);
        sibling.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });
    }

    // Re-target the checkbox so the eventual add-to-cart uses the picked
    // variant. Disable it when the variant is unavailable; if the user had
    // already checked it, untick + clear the selection.
    const checkbox = card.querySelector('.add-to-cart-modal__radio-input');
    if (checkbox instanceof HTMLInputElement) {
      checkbox.dataset.variantId = variantId;
      checkbox.disabled = !available;
      if (!available && checkbox.checked) {
        checkbox.checked = false;
        this.#selectedProductIds.delete(productId);
        this.#setButtonSuccessState(productId, false);
        this.#updateBottomButtons();
        this.#updateSelectedProductsSubtotal();
      }
    }

    // Update the displayed price and the cached price map entry used by the
    // subtotal row. The Liquid `money` filter can return HTML (Transcy wraps
    // the value in a <span class="transcy-money">…) so we use innerHTML —
    // the content is trusted theme-generated markup.
    const priceCurrent = card.querySelector('.add-to-cart-modal__price-current');
    if (priceCurrent instanceof HTMLElement) {
      priceCurrent.innerHTML = priceDisplay;
      if (priceStr) priceCurrent.setAttribute('data-price', priceStr);
      if (compareStr && Number(compareStr) > Number(priceStr)) {
        priceCurrent.setAttribute('data-compare-at-price', compareStr);
      } else {
        priceCurrent.removeAttribute('data-compare-at-price');
      }
    }

    const priceOriginal = card.querySelector('.add-to-cart-modal__price-original');
    const hasCompare = compareStr && priceStr && Number(compareStr) > Number(priceStr);
    if (priceOriginal instanceof HTMLElement) {
      if (hasCompare) {
        priceOriginal.innerHTML = compareDisplay;
        priceOriginal.style.display = '';
      } else {
        priceOriginal.style.display = 'none';
      }
    }

    // Discount badge on the image (if present).
    const badge = card.querySelector('.add-to-cart-modal__product-badge');
    if (badge instanceof HTMLElement) {
      if (hasCompare) {
        const discount = Math.round(
          ((Number(compareStr) - Number(priceStr)) * 100) / Number(compareStr)
        );
        badge.textContent = `-${discount}%`;
        badge.style.display = '';
      } else {
        badge.style.display = 'none';
      }
    }

    // Invalidate any cached price for this product so the next subtotal
    // refresh picks up the new value.
    this.#productPrices.delete(productId);
    if (this.#selectedProductIds.has(productId)) {
      this.#extractProductPrice(productId);
      this.#updateSelectedProductsSubtotal();
    }
  };

  /**
   * Sets the checkbox state to show cart or checkmark icon
   * @param {string} productId - The product ID
   * @param {boolean} isSuccess - Whether to show success state (checkmark)
   */
  #setButtonSuccessState(productId, isSuccess) {
    if (!productId) return;

    const wrapper = this.#getWrapper();
    if (!wrapper) return;
    const checkbox = wrapper.querySelector(`.add-to-cart-modal__radio-input[data-product-id="${productId}"]`);
    if (!checkbox) return;

    const label = checkbox.closest('.add-to-cart-modal__radio-label');
    if (!label) return;

    const checkmarkIcon = label.querySelector('.add-to-cart-modal__icon-checkmark');
    const cartIcon = label.querySelector('.add-to-cart-modal__icon-cart');
    const radioButton = label.querySelector('.add-to-cart-modal__radio-button');

    if (isSuccess) {
      cartIcon?.classList.add('hidden');
      checkmarkIcon?.classList.remove('hidden');
      radioButton?.classList.add('add-to-cart-modal__radio-button--success');
    } else {
      checkmarkIcon?.classList.add('hidden');
      cartIcon?.classList.remove('hidden');
      radioButton?.classList.remove('add-to-cart-modal__radio-button--success');
    }
  }

  /**
   * Updates the bottom buttons based on selected products
   */
  #updateBottomButtons() {
    const wrapper = this.#getWrapper();
    if (!wrapper) return;
    const defaultActions = wrapper.querySelector('.add-to-cart-modal__default-actions');
    const addSelectedWrapper = wrapper.querySelector('.add-to-cart-modal__add-selected-wrapper');

    if (!defaultActions || !addSelectedWrapper) return;

    if (this.#selectedProductIds.size > 0) {
      defaultActions.classList.add('hidden');
      addSelectedWrapper.classList.remove('hidden');
      
      // Update button click handler to use our custom method instead of form submission
      const addSelectedButton = addSelectedWrapper.querySelector('button[ref="addToCartButton"]');
      if (addSelectedButton instanceof HTMLElement) {
        // Remove the default handleClick handler and use our custom one
        const modalId = this.id;
        addSelectedButton.setAttribute('on:click', `#${modalId}/addSelectedToCart`);
        // Change button type to button instead of submit to prevent form submission
        if (addSelectedButton instanceof HTMLButtonElement) {
          addSelectedButton.type = 'button';
        }
      }
      
      // Store the original animation setting before we potentially disable it
      const addToCartComponent = addSelectedWrapper.querySelector('add-to-cart-component');
      if (addToCartComponent instanceof HTMLElement) {
        // Store original value if not already stored
        if (!addToCartComponent.dataset.originalAddToCartAnimation) {
          addToCartComponent.dataset.originalAddToCartAnimation = addToCartComponent.dataset.addToCartAnimation || 'false';
        }
        // Disable the add-to-cart-component's own animation - we handle it manually
        addToCartComponent.setAttribute('data-add-to-cart-animation', 'false');
      }
      
      // Update button price when button is shown
      this.#updateSelectedProductsSubtotal();
    } else {
      defaultActions.classList.remove('hidden');
      addSelectedWrapper.classList.add('hidden');
    }
  }

  /**
   * Fetches the cart to track the original product's item key
   * @param {string} variantId - The variant ID to track
   */
  async #fetchCartForTracking(variantId) {
    try {
      const shopifyRoutes = /** @type {any} */ (window.Shopify)?.routes;
      const cartResponse = await fetch(`${shopifyRoutes?.root || ''}cart.js`);
      
      if (!cartResponse.ok) {
        console.warn('[BUNDLE] Failed to fetch cart for tracking');
        return;
      }

      const cartData = /** @type {any} */ (await cartResponse.json());
      
      // Find the most recently added item with this variant ID
      const matchingItems = cartData.items.filter(
        (/** @type {any} */ item) => String(item.variant_id) === String(variantId)
      );
      
      if (matchingItems.length > 0) {
        // Get the last item (most recently added)
        const originalItem = matchingItems[matchingItems.length - 1];
        
        // Store the unique key (most reliable identifier)
        if (originalItem.key) {
          this.#originalItemKey = originalItem.key;
        }
        
        // Also store line item number as fallback
        const itemIndex = cartData.items.indexOf(originalItem);
        if (itemIndex !== -1) {
          this.#originalLineItem = itemIndex + 1; // Convert to 1-based
        }
      } else {
        console.warn('[BUNDLE] No matching items found in cart for variant', variantId);
      }
    } catch (error) {
      console.error('[BUNDLE] Error fetching cart for tracking:', error);
    }
  }

  /**
   * Removes the original product from cart
   * @returns {Promise<boolean>} True if removal was successful, false otherwise
   */
  async #removeOriginalProduct() {
    if (!this.#originalVariantId && !this.#originalItemKey) {
      console.warn('[BUNDLE] No original product identifier to remove');
      return false;
    }

    try {
      // Fetch current cart to find the exact item
      const shopifyRoutesCart = /** @type {any} */ (window.Shopify)?.routes;
      const cartResponse = await fetch(`${shopifyRoutesCart?.root || ''}cart.js`);
      
      if (!cartResponse.ok) {
        console.error('Failed to fetch cart');
        return false;
      }

      const cartData = /** @type {any} */ (await cartResponse.json());
      
      // Find the item by key (most reliable) or fallback to other methods
      let originalItem = null;
      
      if (this.#originalItemKey) {
        // Use the unique key to find the exact item
        originalItem = cartData.items.find(
          (/** @type {any} */ item) => item.key === this.#originalItemKey
        );
      }
      
      // Fallback to line item number if key not found or not available
      if (!originalItem && this.#originalLineItem) {
        originalItem = cartData.items[this.#originalLineItem - 1]; // Convert to 0-based index
      }
      
      // Fallback to variant ID if key and line not found (less reliable if multiple exist)
      if (!originalItem && this.#originalVariantId) {
        const matchingItems = cartData.items.filter(
          (/** @type {any} */ item) => String(item.variant_id) === String(this.#originalVariantId)
        );
        
        // If we have a line item stored, try to use it as index
        if (this.#originalLineItem && matchingItems.length > 0) {
          // Use the stored line item as a hint (might be the last one if it was just added)
          originalItem = matchingItems[matchingItems.length - 1];
        } else if (matchingItems.length > 0) {
          // Use the first matching item as last resort
          originalItem = matchingItems[0];
        }
      }

      if (!originalItem) {
        console.warn('Original product not found in cart', {
          hasKey: !!this.#originalItemKey,
          hasLineItem: !!this.#originalLineItem,
          hasVariantId: !!this.#originalVariantId
        });
        return false;
      }

      // Get the line item number (1-based) - required for cart/change.js
      const lineItem = originalItem.line || (cartData.items.indexOf(originalItem) + 1);

      // Remove the item by setting quantity to 0
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.#sectionId].filter(Boolean));
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset?.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        line: lineItem,
        quantity: 0,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const shopifyRoutesChange = /** @type {any} */ (window.Shopify)?.routes;
      const response = await fetch(`${shopifyRoutesChange?.root || ''}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: body,
      });

      if (!response.ok) {
        console.error('[BUNDLE] Failed to remove original product from cart - HTTP', response.status);
        return false;
      }

      const data = await response.json();

      if (data.errors) {
        console.error('[BUNDLE] Error removing product:', data.errors);
        return false;
      }

      // Dispatch cart update event
      document.dispatchEvent(
        new CartUpdateEvent(data, this.#sectionId || '', {
          itemCount: data.item_count || 0,
          source: 'add-to-cart-modal',
          sections: data.sections,
        })
      );

      return true;
    } catch (error) {
      console.error('Error removing original product:', error);
      return false;
    }
  }

  /**
   * Adds all selected products to cart
   * @param {Event} event - The click event
   */
  addSelectedToCart = async (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (window.WebHaptics) window.WebHaptics.trigger('success');

    if (this.#selectedProductIds.size === 0) {
      console.warn('No products selected');
      return;
    }

    const productIds = Array.from(this.#selectedProductIds);
    
    const wrapper = this.#getWrapper();
    if (!wrapper) return;

    // Check if this is a bundle recommendation type
    const recommendationsElement = wrapper.querySelector('.product-recommendations');
    const isBundle = recommendationsElement instanceof HTMLElement && recommendationsElement.dataset.intent === 'bundles';
    
    const addSelectedButton = wrapper.querySelector('.add-to-cart-modal__add-selected-button');
    const addToCartComponent = wrapper.querySelector('.add-to-cart-modal__add-selected-wrapper add-to-cart-component');
    // Check the original animation setting (stored before we disabled it)
    const originalAnimation = addToCartComponent instanceof HTMLElement 
      ? (addToCartComponent.dataset.originalAddToCartAnimation || addToCartComponent.dataset.addToCartAnimation)
      : null;
    const animationEnabled = originalAnimation === 'true';
    
    // Disable the button while adding products
    if (addToCartComponent && 'disable' in addToCartComponent && typeof addToCartComponent.disable === 'function') {
      /** @type {any} */ (addToCartComponent).disable();
    }
    
    // Build items array for bulk add
    const items = [];
    const productDataMap = new Map(); // Map to store product data for animations
    
    for (const productId of productIds) {
      const checkbox = wrapper.querySelector(`.add-to-cart-modal__radio-input[data-product-id="${productId}"]`);
      if (!(checkbox instanceof HTMLInputElement)) {
        console.warn(`Checkbox input not found for product ${productId}`);
        continue;
      }

      const variantId = checkbox.dataset.variantId;
      if (!variantId) {
        console.warn(`Variant ID not found for product ${productId}`);
        continue;
      }

      /** @type {{ id: string; quantity: number }} */
      const item = {
        id: variantId,
        quantity: 1,
      };

      items.push(item);
      
      // Store product data for animations
      const productCard = wrapper.querySelector(`.add-to-cart-modal__product-card[data-product-id="${productId}"]`);
      const checkboxLabel = checkbox.closest('.add-to-cart-modal__radio-label');
      productDataMap.set(productId, {
        productCard,
        checkboxLabel,
      });
    }
    
    if (items.length === 0) {
      console.warn('No valid items to add to cart');
      return;
    }
    
    try {
      // Add all products to cart in one request
      /** @type {{ items: Array<{ id: string; quantity: number; properties?: Record<string, string> }>, sections?: string }} */
      const requestBody = {
        items: items,
      };
      
      if (this.#sectionId) {
        requestBody.sections = this.#sectionId;
      }

      const shopifyRoutes = /** @type {any} */ (window.Shopify)?.routes;
      const response = await fetch(`${shopifyRoutes?.root || ''}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        console.error('Failed to add products to cart');
        return;
      }

      const data = await response.json();
      
      if (data.status) {
        // Error occurred
        console.error('Error adding to cart:', data.message || data.description);
        return;
      }

      // Update original item key and line if we can find it in the response
      // This helps if the item wasn't tracked initially
      if (this.#originalVariantId && data.items) {
        const matchingItems = data.items.filter(
          (/** @type {any} */ item) => String(item.variant_id) === String(this.#originalVariantId)
        );
        
        if (matchingItems.length > 0) {
          // Get the last item (most recently added)
          const originalItem = matchingItems[matchingItems.length - 1];
          
          // Store the unique key if not already stored
          if (!this.#originalItemKey && originalItem.key) {
            this.#originalItemKey = originalItem.key;
          }
          
          // Update line item number
          if (originalItem.line !== undefined) {
            this.#originalLineItem = originalItem.line;
          }
        }
      }
      
      // Trigger fly-to-cart animations for each successfully added product
      if (animationEnabled && addSelectedButton instanceof HTMLElement && data.items) {
        data.items.forEach((/** @type {any} */ item, /** @type {number} */ index) => {
          const productId = String(item.product_id || productIds[index]);
          const productData = productDataMap.get(productId);
          
          if (productData && productData.productCard && productData.checkboxLabel instanceof HTMLElement) {
            const productImage = productData.productCard.querySelector('.add-to-cart-modal__product-image img');
            if (productImage instanceof HTMLImageElement) {
              const imageUrl = productImage.src || productImage.getAttribute('srcset')?.split(' ')[0];
              if (imageUrl) {
                // Small delay between each animation (50ms)
                setTimeout(() => {
                  this.#animateFlyToCartForProduct(productId, productData.checkboxLabel, addSelectedButton, index);
                }, index * 50);
              }
            }
          }
        });
      }
      
      // Dispatch cart add event for all items
      document.dispatchEvent(
        new CartAddEvent(data, productIds.join(','), {
          source: 'add-to-cart-modal',
          itemCount: data.items?.length || items.length,
          productId: productIds.join(','),
          sections: data.sections,
        })
      );

      // Remove original product if this is a bundle
      if (isBundle && (this.#originalVariantId || this.#originalItemKey)) {
        const removalSuccess = await this.#removeOriginalProduct();
        if (!removalSuccess) {
          console.warn('[BUNDLE] Failed to remove original product');
        }
        
        // Clear tracking after removal
        this.#originalVariantId = null;
        this.#originalLineItem = null;
        this.#originalItemKey = null;
      } else if (isBundle) {
        console.warn('[BUNDLE] Bundle mode but no original product tracked - cannot remove');
      }
      
      // Open cart drawer after adding products
      setTimeout(() => {
        this.openCartDrawer(new Event('auto-open'));
      }, 300); // Small delay to allow animations to start
    } catch (error) {
      console.error('Error adding products to cart:', error);
    }

    // Clear selections after adding
    this.#selectedProductIds.clear();
    productIds.forEach(id => {
      const checkbox = wrapper.querySelector(`.add-to-cart-modal__radio-input[data-product-id="${id}"]`);
      if (checkbox instanceof HTMLInputElement) {
        checkbox.checked = false;
      }
      this.#setButtonSuccessState(id, false);
    });
    
    // Wait for the last animation to be triggered before hiding the wrapper
    const lastAnimationDelay = (productIds.length - 1) * 50;
    setTimeout(() => {
      // Re-enable the button and show default actions
      if (addToCartComponent && 'enable' in addToCartComponent && typeof addToCartComponent.enable === 'function') {
        /** @type {any} */ (addToCartComponent).enable();
      }
      
      // Update buttons to show default actions again (since selections are cleared)
      this.#updateBottomButtons();
    }, lastAnimationDelay + 100); // Add 100ms buffer to ensure animation is triggered
  }

  /**
   * Animates the fly to cart animation for a specific product
   * @param {string} productId - The product ID
   * @param {HTMLElement} iconElement - The icon element (checkbox label) for the product
   * @param {HTMLElement} sourceButton - The "Add to cart" button that triggered the action
   * @param {number} index - Index of the product in the sequence (for staggering)
   */
  #animateFlyToCartForProduct(productId, iconElement, sourceButton, index) {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon) return;

    const productCard = iconElement.closest('.add-to-cart-modal__product-card');
    if (!productCard) return;

    const productImage = productCard.querySelector('.add-to-cart-modal__product-image img');
    if (!(productImage instanceof HTMLImageElement)) return;

    const imageUrl = productImage.src || productImage.getAttribute('srcset')?.split(' ')[0];
    if (!imageUrl) return;

    // Use the sourceButton (add-selected-button) as the source for animation
    if (!sourceButton || !(sourceButton instanceof HTMLElement)) return;

    // Ensure button is visible
    const buttonWrapper = sourceButton.closest('.add-to-cart-modal__add-selected-wrapper');
    if (buttonWrapper && buttonWrapper.classList.contains('hidden')) {
      buttonWrapper.classList.remove('hidden');
    }

    // Create fly-to-cart element
    const flyToCartElement = /** @type {any} */ (document.createElement('fly-to-cart'));
    flyToCartElement.style.setProperty('background-image', `url(${imageUrl})`);
    flyToCartElement.style.position = 'fixed';
    flyToCartElement.style.left = '0';
    flyToCartElement.style.top = '0';

    // Append animation element immediately (delay is handled by the caller)
    const appendElement = () => {
      const buttonRect = sourceButton.getBoundingClientRect();
      const hasValidDimensions = buttonRect.width > 0 && buttonRect.height > 0;

      if (!hasValidDimensions) {
        requestAnimationFrame(appendElement);
        return;
      }

      // Use the sourceButton (add-selected-button) as source for animation
      flyToCartElement.source = sourceButton;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(appendElement);
    });
  }

  /**
   * Creates an abort controller for fetch requests
   * @returns {AbortController}
   */
  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }
}

if (!customElements.get('add-to-cart-modal-component')) {
  customElements.define('add-to-cart-modal-component', AddToCartModalComponent);
}

